import type { DocSection, DocContent, Language } from './types.js';

// Documentation structure for sidebar navigation
export const docSections: Record<Language, DocSection[]> = {
	'en': [
		{
			id: 'getting-started',
			title: 'Getting Started',
			icon: '🚀',
			items: [
				{
					id: 'introduction',
					title: 'Introduction',
					slug: 'introduction',
					description: 'Welcome to Heyweek - your all-in-one productivity platform'
				},
				{
					id: 'quick-start',
					title: 'Quick Start',
					slug: 'quick-start',
					description: 'Get up and running with Heyweek in under 5 minutes'
				},
				{
					id: 'first-project',
					title: 'Your First Project',
					slug: 'first-project',
					description: 'Create and manage your first project'
				}
			]
		},
		{
			id: 'features',
			title: 'Features',
			icon: '⚡',
			items: [
				{
					id: 'absence',
					title: 'Absence',
					slug: 'absence',
					description: 'Manage time off and leave requests'
				},
				{
					id: 'boards',
					title: 'Boards',
					slug: 'boards',
					description: 'Visual project management with Kanban boards'
				},
				{
					id: 'calendar',
					title: 'Calendar',
					slug: 'calendar',
					description: 'Schedule and manage events and meetings'
				},
				{
					id: 'cashflow',
					title: 'Cashflow',
					slug: 'cashflow',
					description: 'Financial management and cash flow tracking'
				},
				{
					id: 'client-view',
					title: 'Client View',
					slug: 'client-view',
					description: 'Dedicated portal for client collaboration'
				},
				{
					id: 'clients',
					title: 'Clients',
					slug: 'clients',
					description: 'Manage client relationships and information'
				},
				{
					id: 'files',
					title: 'Files',
					slug: 'files',
					description: 'Store, organize, and share your team files'
				},
				{
					id: 'invoicing',
					title: 'Invoicing & Billing',
					slug: 'invoicing',
					description: 'Generate and manage invoices for your clients'
				},
				{
					id: 'integrations',
					title: 'Integrations',
					slug: 'integrations',
					description: 'Connect Heyweek with calendars and bank accounts'
				},
				{
					id: 'messages',
					title: 'Messages',
					slug: 'messages',
					description: 'Internal messaging and team communication'
				},
				{
					id: 'notes',
					title: 'Notes',
					slug: 'notes',
					description: 'Create and organize notes and documentation'
				},
				{
					id: 'spreadsheet',
					title: 'Spreadsheets',
					slug: 'spreadsheet',
					description: 'Collaborative spreadsheets inside your notes (beta)'
				},
				{
					id: 'people',
					title: 'People',
					slug: 'people',
					description: 'Manage team members and contacts'
				},
				{
					id: 'projects',
					title: 'Projects',
					slug: 'projects',
					description: 'Everything about projects'
				},
				{
					id: 'pulse',
					title: 'Pulse',
					slug: 'pulse',
					description: 'Real-time activity feed and updates'
				},
				{
					id: 'reports',
					title: 'Reports',
					slug: 'reports',
					description: 'Analytics and reporting dashboard'
				},
				{
					id: 'tags',
					title: 'Tags',
					slug: 'tags',
					description: 'Organize content with tags and labels'
				},
				{
					id: 'tasks',
					title: 'Tasks',
					slug: 'tasks',
					description: 'Task management fundamentals',
					children: [
						{
							id: 'task-properties',
							title: 'Task Properties',
							slug: 'task-properties',
							description: 'Understanding task fields and properties'
						}
					]
				},
				{
					id: 'teams',
					title: 'Teams',
					slug: 'teams',
					description: 'Team collaboration features',
					children: [
						{
							id: 'team-setup',
							title: 'Team Setup',
							slug: 'team-setup',
							description: 'Setting up your team structure'
						},
						{
							id: 'roles-permissions',
							title: 'Roles & Permissions',
							slug: 'roles-permissions',
							description: 'Managing team roles and access'
						},
						{
							id: 'team-communication',
							title: 'Team Communication',
							slug: 'team-communication',
							description: 'Effective team communication tools'
						}
					]
				},
				{
					id: 'time-tracking',
					title: 'Time Tracking',
					slug: 'time-tracking',
					description: 'Track time spent on tasks and projects'
				},
				{
					id: 'todo',
					title: 'Todo',
					slug: 'todo',
					description: 'Personal todo lists and task management'
				},
				{
					id: 'workspaces',
					title: 'Workspaces',
					slug: 'workspaces',
					description: 'Manage multiple workspaces and organizations'
				}
			]
		},
		{
			id: 'cli',
			title: 'CLI',
			icon: '⌨️',
			items: [
				{
					id: 'cli-overview',
					title: 'CLI Overview',
					slug: 'cli/overview',
					description: 'Introduction to Heyweek CLI'
				},
				{
					id: 'cli-installation',
					title: 'Installation',
					slug: 'cli/installation',
					description: 'Installing Heyweek CLI on your system'
				},
				{
					id: 'cli-configuration',
					title: 'Configuration',
					slug: 'cli/configuration',
					description: 'Configure CLI settings and authentication'
				},
				{
					id: 'cli-commands',
					title: 'Commands',
					slug: 'cli/commands',
					description: 'Complete command reference',
					children: [
						{
							id: 'cli-auth',
							title: 'Authentication',
							slug: 'cli/commands/auth',
							description: 'Login, logout and token management'
						},
						{
							id: 'cli-timer',
							title: 'Timer',
							slug: 'cli/commands/timer',
							description: 'Time tracking commands'
						},
						{
							id: 'cli-project',
							title: 'Projects',
							slug: 'cli/commands/project',
							description: 'Project management commands'
						},
						{
							id: 'cli-client',
							title: 'Clients',
							slug: 'cli/commands/client',
							description: 'Client management commands'
						},
						{
							id: 'cli-log',
							title: 'Logs',
							slug: 'cli/commands/log',
							description: 'Work log commands'
						},
						{
							id: 'cli-workspace',
							title: 'Workspaces',
							slug: 'cli/commands/workspace',
							description: 'Workspace management'
						}
					]
				},
				{
					id: 'cli-workflows',
					title: 'Workflows',
					slug: 'cli/workflows',
					description: 'Common CLI workflows and examples',
					children: [
						{ id: 'cli-workflows-projects-clients', title: 'Projects & Clients', slug: 'cli/workflows/projects-clients', description: 'Project and client workflows' },
						{ id: 'cli-workflows-team-automation', title: 'Team & Automation', slug: 'cli/workflows/team-automation', description: 'Team collaboration and automation' },
						{ id: 'cli-workflows-advanced', title: 'Advanced', slug: 'cli/workflows/advanced', description: 'Advanced and troubleshooting workflows' }
					]
				},
				{
					id: 'cli-scripting',
					title: 'Scripting',
					slug: 'cli/scripting',
					description: 'Automating tasks with CLI scripts',
					children: [
						{ id: 'cli-scripting-shell', title: 'Shell', slug: 'cli/scripting/shell', description: 'Shell scripting' },
						{ id: 'cli-scripting-python', title: 'Python', slug: 'cli/scripting/python', description: 'Python scripting' },
						{ id: 'cli-scripting-nodejs', title: 'Node.js', slug: 'cli/scripting/nodejs', description: 'Node.js scripting' },
						{ id: 'cli-scripting-advanced', title: 'Advanced', slug: 'cli/scripting/advanced', description: 'Advanced scripting patterns' }
					]
				},
				{
					id: 'cli-integrations',
					title: 'Integrations',
					slug: 'cli/integrations',
					description: 'Use the CLI with scripts, CI, and other tools'
				},
				{
					id: 'cli-best-practices',
					title: 'Best Practices',
					slug: 'cli/best-practices',
					description: 'Tips for getting the most out of the CLI'
				},
				{
					id: 'cli-troubleshooting',
					title: 'Troubleshooting',
					slug: 'cli/troubleshooting',
					description: 'Common issues and solutions'
				}
			]
		},
		{
			id: 'browser-extension',
			title: 'Browser Extension',
			icon: '🌐',
			items: [
				{
					id: 'extension-overview',
					title: 'Overview',
					slug: 'browser-extension/overview',
					description: 'Introduction to the Heyweek browser extension'
				},
				{
					id: 'extension-installation',
					title: 'Installation',
					slug: 'browser-extension/installation',
					description: 'Installing the extension in your browser'
				},
				{
					id: 'extension-features',
					title: 'Features',
					slug: 'browser-extension/features',
					description: 'Browser extension features and capabilities'
				},
				{
					id: 'extension-shortcuts',
					title: 'Keyboard Shortcuts',
					slug: 'browser-extension/shortcuts',
					description: 'Quick actions with keyboard shortcuts'
				}
			]
		},
		{
			id: 'mobile',
			title: 'Mobile',
			icon: '📱',
			items: [
				{
					id: 'mobile-overview',
					title: 'Mobile Overview',
					slug: 'mobile/overview',
					description: 'Heyweek on mobile devices'
				},
				{
					id: 'ios-app',
					title: 'iOS App',
					slug: 'mobile/ios',
					description: 'Using Heyweek on iPhone and iPad'
				},
				{
					id: 'android-app',
					title: 'Android App',
					slug: 'mobile/android',
					description: 'Using Heyweek on Android devices'
				},
				{
					id: 'mobile-features',
					title: 'Mobile Features',
					slug: 'mobile/features',
					description: 'Features available on mobile'
				},
				{
					id: 'offline-mode',
					title: 'Offline Mode',
					slug: 'mobile/offline',
					description: 'Working offline with mobile apps'
				},
				{
					id: 'mobile-notifications',
					title: 'Push Notifications',
					slug: 'mobile/notifications',
					description: 'Managing mobile notifications'
				}
			]
		},
		{
			id: 'resources',
			title: 'Resources',
			icon: '📚',
			items: [
				{
					id: 'faq',
					title: 'FAQ',
					slug: 'faq',
					description: 'Frequently asked questions'
				},
				{
					id: 'troubleshooting',
					title: 'Troubleshooting',
					slug: 'troubleshooting',
					description: 'Solutions to common issues'
				},
				{
					id: 'contact-support',
					title: 'Contact Support',
					slug: 'contact-support',
					description: 'Reach out to our support team'
				}
			]
		}
	],
	// Placeholder structure for other languages - in a real implementation, 
	// these would be fully translated
	'sv-SE': [
		{
			id: 'getting-started',
			title: 'Komma igång',
			icon: '🚀',
			items: [
				{
					id: 'introduction',
					title: 'Introduktion',
					slug: 'introduction',
					description: 'Välkommen till Heyweek - din allt-i-ett produktivitetsplattform'
				}
			]
		}
	],
	'fr-FR': [
		{
			id: 'getting-started',
			title: 'Commencer',
			icon: '🚀',
			items: [
				{
					id: 'introduction',
					title: 'Introduction',
					slug: 'introduction',
					description: 'Bienvenue sur Heyweek - votre plateforme de productivité tout-en-un'
				}
			]
		}
	],
	'es-ES': [
		{
			id: 'getting-started',
			title: 'Primeros Pasos',
			icon: '🚀',
			items: [
				{
					id: 'introduction',
					title: 'Introducción',
					slug: 'introduction',
					description: 'Bienvenido a Heyweek - tu plataforma de productividad todo-en-uno'
				}
			]
		}
	],
	'de-DE': [
		{
			id: 'getting-started',
			title: 'Erste Schritte',
			icon: '🚀',
			items: [
				{
					id: 'introduction',
					title: 'Einführung',
					slug: 'introduction',
					description: 'Willkommen bei Heyweek - Ihrer All-in-One-Produktivitätsplattform'
				}
			]
		}
	],
	'ja-JP': [
		{
			id: 'getting-started',
			title: 'はじめに',
			icon: '🚀',
			items: [
				{
					id: 'introduction',
					title: 'イントロダクション',
					slug: 'introduction',
					description: 'Heyweekへようこそ - オールインワン生産性プラットフォーム'
				}
			]
		}
	],
	'zh-CN': [
		{
			id: 'getting-started',
			title: '入门指南',
			icon: '🚀',
			items: [
				{
					id: 'introduction',
					title: '介绍',
					slug: 'introduction',
					description: '欢迎使用Heyweek - 您的一体化生产力平台'
				}
			]
		}
	],
	'it-IT': [
		{
			id: 'getting-started',
			title: 'Iniziare',
			icon: '🚀',
			items: [
				{
					id: 'introduction',
					title: 'Introduzione',
					slug: 'introduction',
					description: 'Benvenuto su Heyweek - la tua piattaforma di produttività all-in-one'
				}
			]
		}
	],
	'ko-KR': [
		{
			id: 'getting-started',
			title: '시작하기',
			icon: '🚀',
			items: [
				{
					id: 'introduction',
					title: '소개',
					slug: 'introduction',
					description: 'Heyweek에 오신 것을 환영합니다 - 올인원 생산성 플랫폼'
				}
			]
		}
	],
	'pt-BR': [
		{
			id: 'getting-started',
			title: 'Começando',
			icon: '🚀',
			items: [
				{
					id: 'introduction',
					title: 'Introdução',
					slug: 'introduction',
					description: 'Bem-vindo ao Heyweek - sua plataforma de produtividade tudo-em-um'
				}
			]
		}
	],
	'vi-VN': [
		{
			id: 'getting-started',
			title: 'Bắt đầu',
			icon: '🚀',
			items: [
				{
					id: 'introduction',
					title: 'Giới thiệu',
					slug: 'introduction',
					description: 'Chào mừng bạn đến với Heyweek - nền tảng năng suất tất cả trong một'
				}
			]
		}
	],
	'pl-PL': [
		{
			id: 'getting-started',
			title: 'Rozpoczynanie pracy',
			icon: '🚀',
			items: [
				{
					id: 'introduction',
					title: 'Wprowadzenie',
					slug: 'introduction',
					description: 'Witamy w Heyweek - Twojej kompleksowej platformie produktywności'
				}
			]
		}
	],
	'nl-NL': [
		{
			id: 'getting-started',
			title: 'Aan de slag',
			icon: '🚀',
			items: [
				{
					id: 'introduction',
					title: 'Introductie',
					slug: 'introduction',
					description: 'Welkom bij Heyweek - uw alles-in-één productiviteitsplatform'
				}
			]
		}
	],
	'nb-NO': [
		{
			id: 'getting-started',
			title: 'Komme i gang',
			icon: '🚀',
			items: [
				{
					id: 'introduction',
					title: 'Introduksjon',
					slug: 'introduction',
					description: 'Velkommen til Heyweek - din alt-i-ett produktivitetsplattform'
				}
			]
		}
	],
	'da-DK': [
		{
			id: 'getting-started',
			title: 'Kom i gang',
			icon: '🚀',
			items: [
				{
					id: 'introduction',
					title: 'Introduktion',
					slug: 'introduction',
					description: 'Velkommen til Heyweek - din alt-i-en produktivitetsplatform'
				}
			]
		}
	],
	'fi-FI': [
		{
			id: 'getting-started',
			title: 'Aloittaminen',
			icon: '🚀',
			items: [
				{
					id: 'introduction',
					title: 'Johdanto',
					slug: 'introduction',
					description: 'Tervetuloa Heyweekiin - kaikki-yhdessä-tuottavuusalustallesi'
				}
			]
		}
	],
	'uk-UA': [
		{
			id: 'getting-started',
			title: 'Початок роботи',
			icon: '🚀',
			items: [
				{
					id: 'introduction',
					title: 'Вступ',
					slug: 'introduction',
					description: 'Ласкаво просимо до Heyweek - вашої універсальної платформи продуктивності'
				}
			]
		}
	]
};

// Documentation sections for the main docs page (with Popular and Basics grouping)
export const docPageSections: Record<Language, DocSection[]> = {
	'en': [
		{
			id: 'popular',
			title: 'Popular',
			icon: '⭐',
			items: [
				{
					id: 'quick-start',
					title: 'Quick Start',
					slug: 'quick-start',
					description: 'Get up and running with Heyweek in under 5 minutes'
				},
				{
					id: 'cli-overview',
					title: 'CLI Documentation',
					slug: 'cli/overview',
					description: 'Command-line interface for Heyweek'
				},
				{
					id: 'projects',
					title: 'Projects',
					slug: 'projects',
					description: 'Everything about managing projects'
				},
				{
					id: 'teams',
					title: 'Teams',
					slug: 'teams',
					description: 'Team collaboration features'
				},
				{
					id: 'time-tracking',
					title: 'Time Tracking',
					slug: 'time-tracking',
					description: 'Track time spent on tasks and projects'
				}
			]
		},
		{
			id: 'basics',
			title: 'Basics',
			icon: '📖',
			items: [
				{
					id: 'introduction',
					title: 'Introduction',
					slug: 'introduction',
					description: 'Welcome to Heyweek'
				},
				{
					id: 'first-project',
					title: 'Your First Project',
					slug: 'first-project',
					description: 'Create and manage your first project'
				},
				{
					id: 'tasks',
					title: 'Tasks',
					slug: 'tasks',
					description: 'Task management fundamentals'
				},
				{
					id: 'invoicing',
					title: 'Invoicing & Billing',
					slug: 'invoicing',
					description: 'Generate and manage invoices'
				},
				{
					id: 'integrations',
					title: 'Integrations',
					slug: 'integrations',
					description: 'Connect with popular tools'
				},
				{
					id: 'faq',
					title: 'FAQ',
					slug: 'faq',
					description: 'Frequently asked questions'
				}
			]
		}
	],
	// Add other languages as needed
	'sv-SE': [
		{
			id: 'popular',
			title: 'Populära',
			icon: '⭐',
			items: []
		},
		{
			id: 'basics',
			title: 'Grundläggande',
			icon: '📖',
			items: []
		}
	]
};

// Sample content for the introduction page
export const docContent: Record<string, DocContent> = {
	'introduction': {
		id: 'introduction',
		title: 'Introduction to Heyweek',
		description: 'Welcome to Heyweek - your all-in-one productivity platform',
		lastUpdated: '2024-01-15',
		tags: ['introduction', 'overview', 'getting-started'],
		content: `
# Introduction to Heyweek

Welcome to **Heyweek** - the comprehensive productivity platform designed to streamline your workflow, enhance team collaboration, and boost your overall efficiency.

## What is Heyweek?

Heyweek is an all-in-one workspace that combines:

- **Project Management**: Organize tasks, set deadlines, and track progress
- **Team Collaboration**: Chat, share files, and work together seamlessly  
- **Time Tracking**: Monitor time spent on tasks and projects
- **Invoicing**: Generate professional invoices and manage billing
- **Communication**: Built-in messaging, video calls, and notifications

## Why Choose Heyweek?

### 🚀 **Streamlined Workflow**
Eliminate the need for multiple tools by centralizing everything in one platform.

### 👥 **Enhanced Collaboration** 
Real-time collaboration features that keep your team in sync.

### 📊 **Powerful Analytics**
Gain insights into productivity patterns and project performance.

### 🔒 **Enterprise Security**
Bank-level security with end-to-end encryption and compliance certifications.

### 🌍 **Global Accessibility**
Available in 17 languages with 24/7 support worldwide.

## Getting Started

Ready to dive in? Here's what you can do next:

1. **[Quick Start Guide](/docs/quick-start)** - Get up and running in minutes
2. **[Your First Project](/docs/first-project)** - Create and manage your first project
3. **[Team Setup](/docs/team-setup)** - Invite team members and set up collaboration

## Need Help?

Our support team is here to help you succeed:

- **[FAQ](/docs/faq)** - Find answers to common questions
- **[Contact Support](/docs/contact-support)** - Reach out for personalized help

---

*Let's make this week productive with Heyweek!*
		`,
		tableOfContents: [
			{ id: 'what-is-heyweek', title: 'What is Heyweek?', level: 2, anchor: 'what-is-heyweek' },
			{ id: 'why-choose-heyweek', title: 'Why Choose Heyweek?', level: 2, anchor: 'why-choose-heyweek' },
			{ id: 'getting-started', title: 'Getting Started', level: 2, anchor: 'getting-started' },
			{ id: 'need-help', title: 'Need Help?', level: 2, anchor: 'need-help' }
		],
		nextPage: {
			title: 'Quick Start Guide',
			slug: 'quick-start'
		}
	}
};

// Re-export loadDocContent from loader module
export { loadDocContent } from './loader';